import { UnstyledButton, UnstyledButtonProps, createStyles, MantineTheme, CSSObject, Loader } from "@mantine/core";
import React, { FC } from "react";

interface RpaButtonPromoProps
  extends UnstyledButtonProps,
    Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled" | "onClick" | "form"> {
  children: React.ReactNode;
  fullWidth?: boolean;
  isLoading?: boolean;
  sx?: CSSObject | ((theme: MantineTheme) => CSSObject);
}

const useStyles = createStyles((theme, { fullWidth }: { fullWidth?: boolean }) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.other.semanticColors.textInverse,
    background: theme.other.semanticColors.bg.brand,
    fontWeight: theme.other.fontWeights.semibold,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    borderRadius: theme.radius.sm,
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.lineHeight,
    border: "none",
    width: fullWidth ? "100%" : "auto",
    transition: "background-color 150ms ease",
    "&:hover": {
      background: theme.other.semanticColors.bg.brandHover,
    },
    "&:active": {
      background: theme.other.semanticColors.bg.brandActive,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
      outlineOffset: 0,
    },
    "&:disabled": {
      background: theme.other.semanticColors.bg.neutralDisabled,
      color: theme.other.semanticColors.text.disabled,
      cursor: "not-allowed",
    },
  },
}));

export const RpaButtonPromo: FC<RpaButtonPromoProps> = ({ children, fullWidth, isLoading, sx, ...props }) => {
  const { classes } = useStyles({ fullWidth });

  const content = React.useMemo(() => {
    if (!isLoading) return children;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Loader size={18} color="currentColor" />
        <div>{children}</div>
      </div>
    );
  }, [children, isLoading]);

  return (
    <UnstyledButton className={classes.root} sx={sx} {...props} {...(isLoading && { disabled: true })}>
      {content}
    </UnstyledButton>
  );
};
